import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Volume2, Tv, Play } from "lucide-react";
import { Link } from "react-router-dom";

// ClientCard Component
const ClientCard = ({ client, audioManager }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const observerRef = useRef(null);

  // Find preview content from sections
  const videoSection = client.sections.find(
    (section) => section.type === "video"
  );
  const overviewSection = client.sections.find(
    (section) => section.type === "overview"
  );
  const brandSection = client.sections.find(
    (section) => section.type === "brand"
  );

  useEffect(() => {
    if (!videoRef.current) return;
    observerRef.current = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observerRef.current.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observerRef.current.observe(videoRef.current);
    return () => observerRef.current && observerRef.current.disconnect();
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setShouldLoadVideo(true);
    audioManager?.playSound("hover", 0);
    if (videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
    }
  };

  return (
    <Link to={`/work/${client.slug}`}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="relative w-full aspect-video overflow-hidden rounded-lg cursor-pointer group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Glass Background */}
        <div className="absolute inset-0 bg-[#111111]/80 backdrop-blur-xl border border-white/5">
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, rgba(111,87,165,0.05), transparent 50%, rgba(1,178,128,0.05))`,
            }}
          />
          <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px]" />
        </div>

        {/* Media Content */}
        {videoSection?.data.videoUrl ? (
          <video
            ref={videoRef}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            src={shouldLoadVideo ? videoSection.data.videoUrl : undefined}
            poster={videoSection.data.posterImage}
            muted
            playsInline
            loop
            loading="lazy"
          />
        ) : (
          <img
            src={
              videoSection?.data.posterImage || client.sections[0]?.data.image
            }
            alt={client.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}

        {/* Dark Overlay */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300
            ${isHovered ? "opacity-0" : "opacity-10"}`}
        />

        {/* Content Overlay */}
        <div className="absolute inset-0 p-6 flex flex-col justify-between">
          {/* Top Row with Icons and Brand Logo */}
          <div className="flex justify-between items-start">
            {/* Type Icons */}
            <div className="flex items-center gap-2">
              {client.type === "sonic-branding" && (
                <div className="bg-primary-green/20 backdrop-blur-sm px-3 py-1 rounded-full">
                  <Volume2 className="w-4 h-4 text-primary-green" />
                </div>
              )}
              {client.type === "tv-commercial" && (
                <div className="bg-primary-green/20 backdrop-blur-sm px-3 py-1 rounded-full">
                  <Tv className="w-4 h-4 text-primary-green" />
                </div>
              )}
            </div>

            {/* Brand Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: isHovered ? 1 : 0.8,
                scale: isHovered ? 1 : 0.9,
              }}
              transition={{ duration: 0.2 }}
              className="w-16 h-16 bg-gradient-to-b from-primary-green/5 via-primary-purple/5 to-transparent backdrop-blur-md rounded-xl p-2 shadow-xl border border-white/10"
            >
              {brandSection?.data.logo ? (
                <img
                  src={brandSection.data.logo}
                  alt={`${client.title} logo`}
                  className="w-full h-full object-contain filter brightness-0 invert opacity-80"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50 text-xs text-center font-medium">
                  {client.title.split(" ")[0]}
                </div>
              )}
            </motion.div>
          </div>

          {/* Bottom Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isHovered ? 1 : 0.9,
              y: isHovered ? 0 : 10,
            }}
            transition={{ duration: 0.2 }}
            className="text-white"
          >
            <h3 className="text-xl font-bold mb-1">{client.title}</h3>
            <p className="text-sm text-white/70">
              {overviewSection?.data.brief}
            </p>
          </motion.div>

          {/* Play Icon on Hover */}
          {isHovered && videoSection?.data.videoUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-primary-green/90 flex items-center justify-center"
            >
              <Play className="w-8 h-8 text-white" />
            </motion.div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

// Utility to chunk array into groups of n
function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

// Helper to get the first available quote from a group of clients
function getGridQuote(group) {
  for (let client of group) {
    const quoteSection = client.sections?.find(
      (section) =>
        section.type === "clientQuote" &&
        (section.data?.gridQuote || section.data?.quote)
    );
    if (quoteSection) {
      // Prefer gridQuote, fall back to quote
      const quote = quoteSection.data.gridQuote || quoteSection.data.quote;
      if (quote) {
        return {
          quote,
          clientName: quoteSection.data.clientName,
          clientTitle: client.title,
        };
      }
    }
  }
  return null;
}

// QuoteBox component
const QuoteBox = ({ quote, clientName, clientTitle }) => (
  <div className="col-span-1 md:col-span-3 my-8 flex justify-center">
    <div className="bg-primary-green/10 border-l-4 border-primary-green px-8 py-6 rounded-lg max-w-2xl w-full text-center mx-auto shadow-lg">
      <p className="text-xl italic text-white mb-4">“{quote}”</p>
      {(clientName || clientTitle) && (
        <div className="text-primary-green text-sm font-semibold">
          {clientName && <span>{clientName}</span>}
          {clientName && clientTitle && <span> — </span>}
          {clientTitle && <span>{clientTitle}</span>}
        </div>
      )}
    </div>
  </div>
);

const ClientGrid = ({ clients, audioManager }) => {
  return (
    <div className="pt-24 sm:pt-32 relative">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-transparent to-[#111111] z-10" />
        {/* Logo Background */}
        <motion.div
          initial={{ opacity: 0, scale: 1.2 }}
          animate={{ opacity: 0.3 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="/HEAR ME OUT - Logo - Purple.svg"
              alt=""
              className="w-[200%] md:w-[150%] max-w-none opacity-50 -mt-[25%]"
              style={{ filter: "blur(2px)" }}
            />
          </div>
        </motion.div>
        {/* Dot Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px] opacity-5 z-20" />
      </div>
      <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <h1 className="text-4xl md:text-7xl font-light mb-6 text-white/90">
            Our <span className="font-bold">Clients</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70">
            Be messy, browse through some of our most memorable projects to
            date. Maybe there's some that you recognize?
          </p>
        </motion.div>
        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <ClientCard
              key={client.slug}
              client={client}
              audioManager={audioManager}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientGrid;
